import React, { useMemo, useCallback } from "react";
import { format } from "date-fns";

interface DayButtonProps {
  date: string;
  selectedDate: string;
  handleSelect: (date: string) => void;
  buttonRef?: React.RefObject<HTMLButtonElement>;
}

// Memoize DayButton component
const DayButton = React.memo<DayButtonProps>(
  ({ date, selectedDate, handleSelect, buttonRef }) => {
    const dayId = useMemo(() => format(date, "M-d"), [date]);
    const dayNumber = useMemo(() => format(date, "d"), [date]);
    const weekDayShort = useMemo(() => format(date, "EEE"), [date]);
    const isSelected = useMemo(
      () => (selectedDate ? format(selectedDate, "M-d") === dayId : false),
      [selectedDate, dayId]
    );

    const buttonClass = useMemo(
      () =>
        `day-button shrink-0 w-12 h-12 2xl:w-14 2xl:h-14 rounded-md flex flex-col items-center justify-center transition ease-in-out duration-200 ${
          isSelected
            ? "bg-indigo-800 text-white shadow"
            : "bg-gray-200/55 text-gray-700 hover:bg-indigo-50 hover:text-indigo-600"
        }`,
      [isSelected]
    );

    const handleClick = useCallback(() => {
      handleSelect(date);
    }, [date, handleSelect]);

    return (
      <button
        ref={buttonRef || null}
        onClick={handleClick}
        data-day={dayId}
        className={buttonClass}
      >
        <h2 className="text-sm 2xl:text-base day-button-text font-normal">
          {dayNumber}
        </h2>
        <h3 className="text-xs 2xl:text-sm tracking-wider font-light day-of-week">
          {weekDayShort}
        </h3>
      </button>
    );
  }
);

DayButton.displayName = "DayButton";

export default DayButton;
